'use client';

/**
 * NotificationPreferencesPanel Component
 *
 * Apple Liquid Glass-styled preferences panel for controlling notification
 * channels, quiet hours, digest mode, and per-category toggles.
 * Designed for use in settings pages across all portals.
 *
 * @see Issue #699 - Notification System Fidelity
 * @see Apple WWDC 2025 - Liquid Glass design language
 */

import EmailIcon from '@mui/icons-material/Email';
import InAppIcon from '@mui/icons-material/NotificationsActive';
import PushIcon from '@mui/icons-material/PhoneAndroid';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SmsIcon from '@mui/icons-material/Sms';
import DigestIcon from '@mui/icons-material/Summarize';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { DigestFrequency } from '@ops-matrix/shared-types';
import { forwardRef, useCallback } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { GlassSelect } from '../GlassSelect';
import { GlassSwitch } from '../GlassSwitch';
import { GlassTextField } from '../GlassTextField';

// ============================================================================
// Types
// ============================================================================

export interface NotificationPreferencesData {
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  inAppEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  digestMode: boolean;
  digestFrequency?: DigestFrequency | string;
  eventPreferences?: Record<string, boolean>;
}

export interface NotificationCategoryConfig {
  key: string;
  label: string;
  description?: string;
}

export interface NotificationPreferencesPanelProps {
  /** Current preferences */
  preferences: NotificationPreferencesData;

  /** Called when any preference changes */
  onChange: (updated: Partial<NotificationPreferencesData>) => void;

  /** Category configurations for per-category toggles */
  categories?: NotificationCategoryConfig[];

  /** Whether the panel is in loading state */
  loading?: boolean;

  /** Whether the panel is read-only */
  readOnly?: boolean;
}

// ============================================================================
// Sub-components
// ============================================================================

interface ChannelRowProps {
  icon: React.ReactElement;
  label: string;
  description: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  isDark: boolean;
  readOnly?: boolean;
}

const ChannelRow: React.FC<ChannelRowProps> = ({ icon, label, description, enabled, onChange, isDark, readOnly }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      py: 1.5,
      px: 2,
      borderRadius: 2,
      '&:hover': {
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
      },
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        color: enabled ? 'primary.main' : 'text.disabled',
        transition: 'all 0.2s ease',
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {description}
      </Typography>
    </Box>
    <GlassSwitch checked={enabled} onChange={(_, checked) => onChange(checked)} disabled={readOnly} />
  </Box>
);

// ============================================================================
// Component
// ============================================================================

export const NotificationPreferencesPanel = forwardRef<HTMLDivElement, NotificationPreferencesPanelProps>(
  ({ preferences, onChange, categories, loading = false, readOnly = false }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { t } = useI18n();

    const handleChannelChange = useCallback(
      (
        channel: keyof Pick<
          NotificationPreferencesData,
          'emailEnabled' | 'smsEnabled' | 'pushEnabled' | 'inAppEnabled'
        >,
        enabled: boolean,
      ) => {
        onChange({ [channel]: enabled });
      },
      [onChange],
    );

    const handleQuietHoursChange = useCallback(
      (field: 'quietHoursStart' | 'quietHoursEnd', value: string) => {
        onChange({ [field]: value });
      },
      [onChange],
    );

    const handleDigestChange = useCallback(
      (digestMode: boolean) => {
        onChange({ digestMode });
      },
      [onChange],
    );

    const handleDigestFrequencyChange = useCallback(
      (frequency: string) => {
        onChange({ digestFrequency: frequency as DigestFrequency });
      },
      [onChange],
    );

    const handleCategoryToggle = useCallback(
      (key: string, enabled: boolean) => {
        onChange({
          eventPreferences: {
            ...preferences.eventPreferences,
            [key]: enabled,
          },
        });
      },
      [onChange, preferences.eventPreferences],
    );

    const sectionSx = {
      borderRadius: 2.5,
      overflow: 'hidden',
      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.55)',
      backdropFilter: 'blur(20px) saturate(150%)',
      WebkitBackdropFilter: 'blur(20px) saturate(150%)',
      border: `0.5px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
      boxShadow: isDark
        ? '0 2px 12px rgba(0,0,0,0.25), inset 0 0.5px 0 rgba(255,255,255,0.04)'
        : '0 2px 12px rgba(0,0,0,0.04), inset 0 0.5px 0 rgba(255,255,255,0.6)',
      mb: 2.5,
    };

    return (
      <Box ref={ref} sx={{ width: '100%', opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        {/* Channels Section */}
        <Box sx={sectionSx}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}>
              {t('notification.preferences.channels')}
            </Typography>
          </Box>
          <Divider sx={{ opacity: isDark ? 0.06 : 0.1 }} />
          <ChannelRow
            icon={<EmailIcon fontSize="small" />}
            label={t('notification.preferences.channelEmail')}
            description={t('notification.preferences.channelEmailDescription')}
            enabled={preferences.emailEnabled}
            onChange={(v) => handleChannelChange('emailEnabled', v)}
            isDark={isDark}
            readOnly={readOnly}
          />
          <ChannelRow
            icon={<SmsIcon fontSize="small" />}
            label={t('notification.preferences.channelSms')}
            description={t('notification.preferences.channelSmsDescription')}
            enabled={preferences.smsEnabled}
            onChange={(v) => handleChannelChange('smsEnabled', v)}
            isDark={isDark}
            readOnly={readOnly}
          />
          <ChannelRow
            icon={<PushIcon fontSize="small" />}
            label={t('notification.preferences.channelPush')}
            description={t('notification.preferences.channelPushDescription')}
            enabled={preferences.pushEnabled}
            onChange={(v) => handleChannelChange('pushEnabled', v)}
            isDark={isDark}
            readOnly={readOnly}
          />
          <ChannelRow
            icon={<InAppIcon fontSize="small" />}
            label={t('notification.preferences.channelInApp')}
            description={t('notification.preferences.channelInAppDescription')}
            enabled={preferences.inAppEnabled}
            onChange={(v) => handleChannelChange('inAppEnabled', v)}
            isDark={isDark}
            readOnly={readOnly}
          />
        </Box>

        {/* Quiet Hours Section */}
        <Box sx={sectionSx}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ScheduleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}>
                {t('notification.preferences.quietHours')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('notification.preferences.quietHoursDescription')}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ opacity: isDark ? 0.06 : 0.1 }} />
          <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 2 }}>
            <GlassTextField
              label={t('notification.preferences.quietHoursStart')}
              type="time"
              size="small"
              value={preferences.quietHoursStart ?? '22:00'}
              onChange={(e) => handleQuietHoursChange('quietHoursStart', e.target.value)}
              disabled={readOnly}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
            />
            <GlassTextField
              label={t('notification.preferences.quietHoursEnd')}
              type="time"
              size="small"
              value={preferences.quietHoursEnd ?? '08:00'}
              onChange={(e) => handleQuietHoursChange('quietHoursEnd', e.target.value)}
              disabled={readOnly}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
            />
          </Box>
        </Box>

        {/* Digest Section */}
        <Box sx={sectionSx}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <DigestIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}>
                  {t('notification.preferences.digest')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('notification.preferences.digestDescription')}
                </Typography>
              </Box>
            </Box>
            <GlassSwitch
              checked={preferences.digestMode}
              onChange={(_, checked) => handleDigestChange(checked)}
              disabled={readOnly}
            />
          </Box>
          {preferences.digestMode && (
            <>
              <Divider sx={{ opacity: isDark ? 0.06 : 0.1 }} />
              <Box sx={{ px: 2, py: 1.5 }}>
                <GlassSelect
                  label={t('notification.preferences.digestFrequency')}
                  value={preferences.digestFrequency ?? 'daily'}
                  onChange={(e) => handleDigestFrequencyChange(e.target.value as string)}
                  size="small"
                  disabled={readOnly}
                  options={[
                    { value: 'daily', label: t('notification.preferences.digestDaily') },
                    { value: 'weekly', label: t('notification.preferences.digestWeekly') },
                    { value: 'monthly', label: t('notification.preferences.digestMonthly') },
                  ]}
                  sx={{ maxWidth: 200 }}
                />
              </Box>
            </>
          )}
        </Box>

        {/* Categories Section */}
        {categories && categories.length > 0 && (
          <Box sx={sectionSx}>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}>
                {t('notification.preferences.categories')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('notification.preferences.categoriesDescription')}
              </Typography>
            </Box>
            <Divider sx={{ opacity: isDark ? 0.06 : 0.1 }} />
            {categories.map((category) => (
              <Box
                key={category.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.25,
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.82rem' }}>
                    {category.label}
                  </Typography>
                  {category.description && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {category.description}
                    </Typography>
                  )}
                </Box>
                <GlassSwitch
                  checked={preferences.eventPreferences?.[category.key] ?? true}
                  onChange={(_, checked) => handleCategoryToggle(category.key, checked)}
                  disabled={readOnly}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  },
);

NotificationPreferencesPanel.displayName = 'NotificationPreferencesPanel';

export default NotificationPreferencesPanel;
